/**
 * Roles Table Component
 * 
 * Displays roles in a table format with actions.
 * Follows UI/UX guidelines for Table component usage.
 */

import { Users, FilePen, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Table, TableActionButton, TableActions } from '@/components/ui';
import type { Role } from '@/services/RolesService';
import type { TableColumn } from '@/components/ui/Table/Table';
import { PERMISSION_METADATA } from '../../../../server/lib/permissions/permissionRegistry';

interface RolesTableProps {
  roles: Role[];
  loading?: boolean;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  onCreate: () => void;
}

export function RolesTable({
  roles,
  loading = false,
  onEdit,
  onDelete,
  onCreate,
}: RolesTableProps) {
  const columns: TableColumn<Role>[] = [
    {
      key: 'name',
      title: 'Role Name',
      sortable: true,
      render: (_, role) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{role.name}</span>
          {role.is_system && (
            <Badge variant="secondary">System</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      title: 'Description',
      render: (_, role) => (
        <span className="text-sm text-gray-600">
          {role.description || '-'}
        </span>
      ),
    },
    {
      key: 'permissions',
      title: 'Permissions',
      render: (_, role) => (
        <div className="flex flex-wrap gap-1">
          {role.permissions.slice(0, 3).map((permission) => (
            <Badge key={permission} variant="outline" className="text-xs">
              {PERMISSION_METADATA[permission as keyof typeof PERMISSION_METADATA]?.label || permission}
            </Badge>
          ))}
          {role.permissions.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{role.permissions.length - 3} more
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_, role) => (
        <TableActions>
          <TableActionButton
            icon={FilePen}
            onClick={() => onEdit(role)}
          >
            {role.is_system ? 'View' : 'Edit'}
          </TableActionButton>
          {!role.is_system && (
            <TableActionButton
              variant="destructive"
              icon={Trash2}
              onClick={() => onDelete(role)}
            >
              Delete
            </TableActionButton>
          )}
        </TableActions>
      ),
    },
  ];

  // Empty state
  if (!loading && roles.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-12 w-12" />}
        title="No roles found"
        description="Get started by creating a new role or run the seeding script."
        action={{
          label: 'Create Your First Role',
          onClick: onCreate,
        }}
      />
    );
  }

  // Use the reusable Table component with mobile-friendly horizontal scrolling
  return (
    <Table
      data={roles}
      columns={columns}
      loading={loading}
      searchable={true}
      searchPlaceholder="Search roles..."
      pagination={{ pageSize: 10 }}
      emptyText="No roles found"
    />
  );
}

